// Barcode detector acquisition + capability probe.
//
// The native BarcodeDetector Web API is Chromium-only (Android Chrome, desktop
// Chrome/Edge). Every iOS browser runs on WebKit, which does NOT implement it,
// so on iPhone/iPad we fall back to the `barcode-detector` ponyfill (ZXing-C++
// compiled to WebAssembly). Native is always preferred when present so those
// devices never download the WASM.
//
// The ponyfill JS + its ~1.1 MB .wasm are loaded via a dynamic import() so they
// stay out of the main bundle and only download on non-native browsers when the
// scanner is actually opened. The .wasm is self-hosted same-origin under
// /wasm/ (populated at build time by frontend/scripts/copy-wasm.js).

const FORMATS = ['ean_13', 'ean_8', 'code_128'];

/**
 * True when the browser exposes a camera capture API. The ponyfill covers the
 * decoding side on every engine, so a working camera is the only hard
 * requirement for scanning. Synchronous and side-effect-free — safe to call on
 * every render.
 */
export function canScanBarcodes() {
  return (
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices &&
    typeof navigator.mediaDevices.getUserMedia === 'function'
  );
}

/** True when the browser implements the native BarcodeDetector API. */
export function hasNativeBarcodeDetector() {
  return typeof window !== 'undefined' && 'BarcodeDetector' in window;
}

let ponyfillPromise = null;

function loadPonyfillDetector() {
  if (!ponyfillPromise) {
    ponyfillPromise = import('barcode-detector/ponyfill').then(async ({ BarcodeDetector, prepareZXingModule }) => {
      const base = process.env.PUBLIC_URL || '';
      // Serve the ZXing wasm from our own origin instead of the default CDN.
      // fireImmediately: instantiate the module now so the scanner dialog's
      // loading spinner covers the full WASM download+init, rather than the
      // first detect() call stalling.
      await prepareZXingModule({
        overrides: {
          locateFile: (path, prefix) =>
            path.endsWith('.wasm') ? `${base}/wasm/${path}` : prefix + path,
        },
        fireImmediately: true,
      });
      return BarcodeDetector;
    });
  }
  return ponyfillPromise;
}

/**
 * Resolve a BarcodeDetector instance: native when available, otherwise the
 * lazily-loaded WASM ponyfill. May trigger a WASM download on first use.
 */
export async function createBarcodeDetector() {
  if (hasNativeBarcodeDetector()) {
    return new window.BarcodeDetector({ formats: FORMATS });
  }
  const PonyfillBarcodeDetector = await loadPonyfillDetector();
  return new PonyfillBarcodeDetector({ formats: FORMATS });
}
